<script setup lang="ts">
  import { arrow, autoUpdate, flip, offset, shift, type Side, useFloating } from '@floating-ui/vue';
  import { useMouseInElement } from '@vueuse/core';
  import { computed, ref } from 'vue';

  export type TooltipSide = Side;

  const ARROW_OFFSET_PX = 6;
  const OFFSET_DISTANCE_PX = 12;

  const SIDE_MAP: Record<TooltipSide, TooltipSide> = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  export interface TooltipSlots {
    /**
     * The content to display in the tooltip. This may be text node, an element, or a component.
     */
    content: any;
    /**
     * The default slot is the element or component to which the tooltip will be anchored.
     */
    default: any;
    /**
     * A slot for the primary icon. This icon will be displayed before the text or content.
     */
    icon: any;
    /**
     * A slot for the secondary icon. This icon will be displayed after the text or content.
     */
    secondaryIcon: any;
  }

  defineSlots<TooltipSlots>();

  export interface TooltipProps {
    /**
     * Disables teleporting the popover menu to an external element
     * @default false
     */
    disableTeleport?: boolean;
    /**
     * Sets the placement of the menu
     * @default 'top'
     */
    side?: TooltipSide;
    /**
     * Disables the tooltip
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Sets if the menu is open. If not set from the parent context, the menu will be controlled internally.
     * @default false
     */
    isOpen?: boolean;
    /**
     * The query selector where the tooltip should be teleported
     * @default '#stash-menus-mount-node'
     */
    teleportTo?: string | HTMLElement;
    /**
     * String content to display within the tooltip
     * @default ''
     */
    text?: string;
  }

  const props = withDefaults(defineProps<TooltipProps>(), {
    disableTeleport: false,
    isDisabled: false,
    isOpen: false,
    side: 'top',
    teleportTo: '#stash-menus-mount-node',
    text: '',
  });

  const anchor = ref<HTMLElement | null>(null);
  const floatingArrow = ref<HTMLElement | null>(null);
  const tooltip = ref<HTMLElement | null>(null);

  const { isOutside } = useMouseInElement(anchor);

  const open = computed(() => !props.isDisabled && (!isOutside.value || props.isOpen));
  const placementSetting = computed(() => props.side);

  const { floatingStyles, middlewareData, placement } = useFloating(anchor, tooltip, {
    whileElementsMounted: autoUpdate,
    placement: placementSetting,
    middleware: [
      flip(),
      shift({ padding: 8 }),
      offset(OFFSET_DISTANCE_PX),
      arrow({ element: floatingArrow, padding: 8 }),
    ],
  });

  const arrowStyles = computed(() => {
    const arrowPosition = SIDE_MAP[placement.value];

    return {
      left: middlewareData.value.arrow?.x != null ? `${middlewareData.value.arrow.x}px` : '',
      top: middlewareData.value.arrow?.y != null ? `${middlewareData.value.arrow.y}px` : '',
      [arrowPosition]: `-${ARROW_OFFSET_PX}px`,
    };
  });
</script>

<template>
  <div class="stash-tooltip__wrapper relative inline-flex size-fit">
    <span ref="anchor" data-test="stash-tooltip|anchor" class="stash-tooltip__anchor">
      <slot></slot>
    </span>
    <Teleport :to="props.teleportTo" :disabled="props.disableTeleport">
      <div
        ref="tooltip"
        data-test="stash-tooltip"
        class="stash-tooltip pointer-events-none z-screen flex w-[148px] flex-col items-center whitespace-normal rounded bg-ice-900 p-3 text-center text-xs text-white opacity-0 shadow transition-opacity"
        role="tooltip"
        :style="{
          ...floatingStyles,
          opacity: open ? 0.95 : 0,
        }"
      >
        <slot name="icon"></slot>
        <slot name="content">{{ props.text }}</slot>
        <slot name="secondaryIcon"></slot>
        <div
          ref="floatingArrow"
          class="stash-tooltip__arrow absolute z-behind size-[12px] rotate-45 bg-ice-900"
          :style="arrowStyles"
        ></div>
      </div>
    </Teleport>
  </div>
</template>
